import React, { useState } from "react";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Row from "@amzn/meridian/esm/components/row";
import Icon from "@amzn/meridian/esm/components/icon";
import badgeModerate from "../../images/badge_moderate.png";
import badgeDetailed from "../../images/badge_detailed.png";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import SummaryBodyInDetailPage from "../../components/summary/SummaryBodyInDetailPage";
import SummaryFeedback from "../../components/feedback/SummaryFeedback";
import SummaryListButton from "../../components/summary/SummaryListButton";
import SummaryNotApprovedFeedback from "../../components/feedback/SummaryNotApprovedFeedback";
import { SUMMARY_FEEDBACK_STATUS, SUMMARY_TYPES } from "../../constants";
import { VERBOSITY_LEVELS } from "../../constants";
import LogManager from "../../utils/logManager";
import AttendeeListInDetailPage from "../../components/summary/AttendeeListInDetailPage";
import MeetingParticipants from "../../components/summary/MeetingParticipants";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";

/**
 * SummaryDetail - Detail view for a selected summary.
 * Same content as inMeeting/summary/SummaryDetails.jsx but without AimsMasthead.
 * Uses onBack callback instead of route navigation.
 */
const SummaryDetail = ({ summary, onBack, isSessionActive }) => {
  const featureFlags = useFeatureFlags();
  const [showParticipants, setShowParticipants] = useState(false);

  if (!summary) {
    LogManager.error("No summary in SummaryDetail view. This should not happen.");
    return (
      <Column>
        <Text type="b200" color="error">
          Summary not found.
        </Text>
      </Column>
    );
  }

  const summaryFeedbackStatus = SUMMARY_TYPES.APPROVED.includes(summary?.summaryType) 
    ? SUMMARY_FEEDBACK_STATUS.APPROVED
    : SUMMARY_TYPES.NOT_APPROVED.includes(summary?.summaryType)
    ? SUMMARY_FEEDBACK_STATUS.NOT_APPROVED
    : SUMMARY_FEEDBACK_STATUS.OTHER;

  const showFeedback = (!summary.feedback && !summary.feedbackComment) && summaryFeedbackStatus !== SUMMARY_FEEDBACK_STATUS.OTHER;

  const isAttendanceReportEnabled = featureFlags?.isAttendanceReportEnabled;

  // If showing participants view, render MeetingParticipants
  if (showParticipants) {
    return (
      <Column spacing="none" height={isSessionActive ? "calc(100vh - 131px)" : "calc(100vh - 75px)"} heights={["fit", "fill"]}>
        <Column height="16px" />
        <Column width="362px" spacing="none" spacingInset="0 500" overflowY="scroll">
          <MeetingParticipants
            attendees={summary.attendees}
            onBack={() => setShowParticipants(false)}
          />
        </Column>
      </Column>
    );
  }

  return (
    <Column spacing="none" height={isSessionActive ? "calc(100vh - 131px)" : "calc(100vh - 75px)"} heights={showFeedback ? ["fit", "fill", "fit"] : ["fit", "fill"]}>
      <Column spacing="none">
        <Column height="16px" />
        {/* "Summary list" back button */}
        <SummaryListButton onSummaryListInClient={onBack} />
        <Column height="24px" />
      </Column>

      <Column width="362px" spacing="none" spacingInset="0 500" overflowY="scroll">
          <Column spacing="none" spacingInset="none">
            {/* Summary title */}
            <Text type="h200">
              {summary.meetingTitle}
            </Text>
            <Column height="20px" />

            {/* Summary time with calendar icon */}
            <Row spacing="none" spacingInset="none">
              <Icon tokens={calendarTokens} color="secondary" />
              <Row width="4px" />
              <Text type="b100" color="secondary" className="summary-time">
                {summary.meetingDateAndTime}
              </Text>
            </Row>

            <Column height="8px" />

            {/* Attendee list */}
            <AttendeeListInDetailPage
              attendeeNameList={summary.attendeeNameList}
              attendees={summary.attendees}
              isAttendanceReportEnabled={isAttendanceReportEnabled}
              onViewParticipants={() => setShowParticipants(true)}
            />

            {/* Divider */}
            <Column height="16px" />
            <Divider spacingBefore="0" spacingAfter="0" />
            <Column height="16px" />

            {summary.summaryDetailLevel && summary.summaryDetailLevel !== VERBOSITY_LEVELS.CONCISE && (
              <>
                <img
                  src={summary.summaryDetailLevel === VERBOSITY_LEVELS.MODERATE ? badgeModerate : badgeDetailed}
                  alt={summary.summaryDetailLevel}
                  width={90}
                  height={20}
                />
                <Column height="16px" />
              </>
            )}

            <SummaryBodyInDetailPage summary={summary} isTopicNotApproved={summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED}/>
          </Column>
      </Column>

      {/* Feedback */}
      {showFeedback && (
        <Column width="362px" spacingInset="0 300">
          {summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.APPROVED ? (
            <SummaryFeedback aimsUuid={summary.aimsUuid} />
          ) : (
            <SummaryNotApprovedFeedback aimsUuid={summary.aimsUuid} />
          )}
        </Column>
      )}
    </Column>
  );
};

export default SummaryDetail;
